import { useEffect, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingIntradayFeeAdjustedPriceQuerySchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import { previewBiddingIntradayOrderFeeAdjustedPrices } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { PreviewBiddingIntradayOrderFeeAdjustedPricesRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { skipToken } from '@tanstack/react-query'

import type { IntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import {
  type FeeAdjustedPricePreviewQueryStatus,
  type FeeAutoConsiderPreview,
  previewQueryStatus,
  resolveFeeAutoConsiderPreview,
} from '~/pages/IntradayBidding/models/feeAutoConsider'

export type IntradayFeeAdjustedPriceInput = {
  areaCode: AreaCode
  direction: Side
  desiredPriceYenPerKwh: string
}

/** 価格を打ち終えるまで問い合わせを待つ時間。 */
const PREVIEW_DEBOUNCE_MS = 300

const keyOf = (input: IntradayFeeAdjustedPriceInput | undefined): string =>
  input === undefined
    ? ''
    : `${input.areaCode}:${input.direction}:${input.desiredPriceYenPerKwh}`

/**
 * 手数料自動考慮 (時間前) の入札価格を backend のプレビュー RPC で引く (AC-BIN-004b / #9213)。
 * `input` が undefined の間 (入力が揃っていない・固定価格でない) と、 OFF として扱う間 (入力値が
 * そのまま入札価格) は問い合わせない。 入力が止まってから問い合わせ、 入力が最新の結果に追いつくまでは
 * 作成・保存を止める。 判定は `fee` に渡し、 手数料単価を使えないと示したら OFF に切り替わる。
 * 一括作成は全札が同じエリア・売買・価格なので 1 行で問い合わせる。
 */
export function useIntradayFeeAdjustedPricePreview(
  input: IntradayFeeAdjustedPriceInput | undefined,
  fee: IntradayFeeTreatment,
): FeeAutoConsiderPreview {
  const key = keyOf(input)
  const [settledKey, setSettledKey] = useState(key)
  useEffect(() => {
    const id = setTimeout(() => setSettledKey(key), PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [key])
  const query =
    input !== undefined &&
    settledKey === key &&
    fee.treatment.status !== 'fallbackOff'
      ? input
      : undefined

  const { data, isFetching, isError, failureCount } = useQuery(
    previewBiddingIntradayOrderFeeAdjustedPrices,
    query === undefined
      ? skipToken
      : create(PreviewBiddingIntradayOrderFeeAdjustedPricesRequestSchema, {
          queries: [
            create(BiddingIntradayFeeAdjustedPriceQuerySchema, {
              areaCode: query.areaCode,
              direction: query.direction,
              desiredPriceYenPerKwh: { value: query.desiredPriceYenPerKwh },
            }),
          ],
        }),
    { throwOnError: false },
  )
  const status: FeeAdjustedPricePreviewQueryStatus =
    input === undefined
      ? 'idle'
      : query === undefined
        ? 'loading'
        : previewQueryStatus({
            waiting: isFetching,
            isError,
            failureCount,
            hasData: data !== undefined,
          })
  const preview = resolveFeeAutoConsiderPreview({
    status,
    state: data?.feeAutoConsider,
    result: data?.results[0],
    side: input?.direction ?? Side.UNSPECIFIED,
    treatment: fee.treatment,
  })
  fee.report(preview)
  return preview
}
